import { useState } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import { useLenis } from "lenis/react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { label: "Riders", href: "#riders", testid: "nav-link-riders" },
  { label: "Drivers", href: "#drivers", testid: "nav-link-drivers" },
  { label: "Features", href: "#features", testid: "nav-link-features" },
  { label: "About", href: "#about", testid: "nav-link-about" },
  { label: "Tips & Impact", href: "/where-your-tips-go", testid: "nav-link-tips", route: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  const lenis = useLenis();
  const location = useLocation();
  const navigate = useNavigate();

  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  const go = (e, link) => {
    e.preventDefault();
    setOpen(false);
    const href = typeof link === "string" ? link : link.href;
    const isRoute = typeof link === "object" && link.route;
    if (isRoute) {
      navigate(href);
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/${href}`);
      return;
    }
    if (lenis) lenis.scrollTo(href, { offset: -72, duration: 1.4 });
    else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 border-b ${
        scrolled || location.pathname !== "/"
          ? "bg-black/70 backdrop-blur-xl border-white/10"
          : "bg-transparent border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-10 h-[72px]">
        <a
          href="/"
          onClick={(e) => go(e, "#top")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <img
            src="/logo.jpg"
            alt="EzRueda logo"
            className="w-10 h-10 rounded-xl object-cover ring-1 ring-white/20 group-hover:ring-brand-yellow transition-shadow duration-300"
          />
          <span className="font-display font-semibold text-xl tracking-tight">
            Ez<span className="text-brand-yellow">Rueda</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l)}
              data-testid={l.testid}
              className={`text-sm font-medium transition-colors duration-300 tracking-wide ${
                l.route && location.pathname === l.href
                  ? "text-brand-yellow"
                  : "text-zinc-400 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden md:block">
          <a
            href="#download"
            onClick={(e) => go(e, "#download")}
            data-testid="nav-cta-button"
            className="inline-flex items-center gap-2 rounded-full bg-brand-yellow text-black text-sm font-bold px-6 py-2.5 hover:bg-brand-yellow-hover hover:scale-105 transition-all duration-300"
          >
            Get the app
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        <button
          className="md:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          data-testid="nav-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="md:hidden bg-black/90 backdrop-blur-xl border-t border-white/10 px-6 py-6 flex flex-col gap-5"
          data-testid="nav-mobile-menu"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l)}
              data-testid={`${l.testid}-mobile`}
              className="text-lg font-display font-medium text-zinc-200"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#download"
            onClick={(e) => go(e, "#download")}
            data-testid="nav-cta-button-mobile"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-yellow text-black font-bold px-6 py-3 mt-2"
          >
            Get the app
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </motion.div>
      )}
    </motion.header>
  );
}
